import React, { useState } from 'react';
import { Mic, Users, Calendar, Zap, TrendingUp } from 'lucide-react';
import BadgePreview from './BadgePreview';

export default function EarningsCalculatorTab({ balance }) {
  const [hostsPerWeek, setHostsPerWeek] = useState(1);
  const [invitesPerWeek, setInvitesPerWeek] = useState(4);
  const [attendsPerWeek, setAttendsPerWeek] = useState(5);

  const tokenPrice = 0.40;
  const minWithdrawalUSD = 100;

  // Calculate earnings
  const weeklyTokens = (hostsPerWeek * 1.0) + (invitesPerWeek * 0.25) + (attendsPerWeek * 0.10);
  const monthlyTokens = weeklyTokens * 4.33;
  const yearlyTokens = weeklyTokens * 52;

  const weeklyUSD = weeklyTokens * tokenPrice;
  const monthlyUSD = monthlyTokens * tokenPrice;
  const yearlyUSD = yearlyTokens * tokenPrice;

  const weeksToWithdrawal = weeklyUSD > 0 ? Math.ceil(minWithdrawalUSD / weeklyUSD) : 0;

  // Dynamic tip
  const getTip = () => {
    if (hostsPerWeek === 0) return "Host events to maximize earnings";
    if (invitesPerWeek < 4) return "Invite 4 people = 1 token";
    if (weeklyTokens >= 10) return `You're on track to withdraw in ${weeksToWithdrawal} weeks`;
    return "Keep building your activity to reach $100 faster!";
  };

  const setPreset = (preset) => {
    switch (preset) {
      case 'casual':
        setHostsPerWeek(1);
        setInvitesPerWeek(2);
        setAttendsPerWeek(3);
        break;
      case 'active':
        setHostsPerWeek(3);
        setInvitesPerWeek(8);
        setAttendsPerWeek(7);
        break;
      case 'power':
        setHostsPerWeek(7);
        setInvitesPerWeek(20);
        setAttendsPerWeek(14);
        break;
    }
  };

  return (
    <div className="p-6 space-y-6 max-h-[600px] overflow-y-auto">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-1">Earnings Calculator</h2>
        <p className="text-gray-400">See what you could earn</p>
      </div>

      {/* Behavior Presets */}
      <div className="flex gap-2">
        <button
          onClick={() => setPreset('casual')}
          className="flex-1 py-2 px-3 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors text-sm"
        >
          Casual
          <span className="text-xs text-gray-400 ml-1">(~1.55/wk)</span>
        </button>
        <button
          onClick={() => setPreset('active')}
          className="flex-1 py-2 px-3 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors text-sm"
        >
          Active
          <span className="text-xs text-gray-400 ml-1">(~5.70/wk)</span>
        </button>
        <button
          onClick={() => setPreset('power')}
          className="flex-1 py-2 px-3 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors text-sm"
        >
          Power User
          <span className="text-xs text-gray-400 ml-1">(~13.40/wk)</span>
        </button>
      </div>

      {/* Input Sliders */}
      <div className="space-y-6">
        {/* Host Events */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-[#8b5cf6]" />
              <span className="text-white font-medium">Events you'll host</span>
            </div>
            <span className="text-[#8b5cf6] font-semibold">= {(hostsPerWeek * 1.0).toFixed(2)} rewards</span>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0"
              max="21"
              step="1"
              value={hostsPerWeek}
              onChange={(e) => setHostsPerWeek(parseInt(e.target.value))}
              className="flex-1 h-2 bg-white/10 rounded-full appearance-none cursor-pointer accent-[#8b5cf6]"
            />
            <span className="text-white font-mono w-8 text-right">{hostsPerWeek}</span>
          </div>
          <p className="text-xs text-gray-400">Max 3 per day x 7 days = 21 per week</p>
        </div>

        {/* Invite Attendees */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#00c853]" />
              <span className="text-white font-medium">People you'll invite</span>
            </div>
            <span className="text-[#00c853] font-semibold">= {(invitesPerWeek * 0.25).toFixed(2)} rewards</span>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0"
              max="50"
              step="1"
              value={invitesPerWeek}
              onChange={(e) => setInvitesPerWeek(parseInt(e.target.value))}
              className="flex-1 h-2 bg-white/10 rounded-full appearance-none cursor-pointer accent-[#00c853]"
            />
            <span className="text-white font-mono w-8 text-right">{invitesPerWeek}</span>
          </div>
          <p className="text-xs text-gray-400">Up to 50 invites per week</p>
        </div>

        {/* Attend Events */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#3b82f6]" />
              <span className="text-white font-medium">Events you'll attend</span>
            </div>
            <span className="text-[#3b82f6] font-semibold">= {(attendsPerWeek * 0.10).toFixed(2)} rewards</span>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0"
              max="35"
              step="1"
              value={attendsPerWeek}
              onChange={(e) => setAttendsPerWeek(parseInt(e.target.value))}
              className="flex-1 h-2 bg-white/10 rounded-full appearance-none cursor-pointer accent-[#3b82f6]"
            />
            <span className="text-white font-mono w-8 text-right">{attendsPerWeek}</span>
          </div>
          <p className="text-xs text-gray-400">Up to 35 events per week</p>
        </div>
      </div>

      {/* Results */}
      <div className="bg-gradient-to-br from-[#8b5cf6]/20 to-[#00c853]/10 border border-white/10 rounded-xl">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#8b5cf6]" />
            <h3 className="text-white font-semibold">Your Projected Earnings</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-300">Weekly:</span>
              <div className="text-right">
                <span className="text-white font-bold text-lg">{weeklyTokens.toFixed(2)} rewards</span>
                <span className="text-gray-400 text-sm ml-2">(~${weeklyUSD.toFixed(2)})</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-300">Monthly:</span>
              <div className="text-right">
                <span className="text-white font-bold text-lg">{monthlyTokens.toFixed(2)} rewards</span>
                <span className="text-gray-400 text-sm ml-2">(~${monthlyUSD.toFixed(2)})</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-300">Yearly:</span>
              <div className="text-right">
                <span className="text-white font-bold text-lg">{yearlyTokens.toFixed(2)} rewards</span>
                <span className="text-gray-400 text-sm ml-2">(~${yearlyUSD.toFixed(2)})</span>
              </div>
            </div>
            {weeksToWithdrawal > 0 && (
              <div className="pt-3 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Time to $100 withdrawal:</span>
                  <span className="text-[#8b5cf6] font-semibold">~{weeksToWithdrawal} weeks</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Power User Tip */}
      <div className="bg-[#8b5cf6]/10 border border-[#8b5cf6]/30 rounded-xl">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-[#8b5cf6] mt-0.5" />
            <p className="text-white text-sm">{getTip()}</p>
          </div>
        </div>
      </div>

      {/* Badge Preview */}
      <BadgePreview balance={balance} />

      {/* Quick Reference */}
      <div className="bg-white/5 border border-white/10 rounded-xl">
        <div className="p-4">
          <h4 className="text-white font-medium mb-3">How digital rewards work:</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Host an event</span>
              <span className="text-white font-medium">1 token</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Invite 4 people</span>
              <span className="text-white font-medium">1 token</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Attend 10 events</span>
              <span className="text-white font-medium">1 token</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
